/**
 * @internal
 * Internal layout section used by BarChart. Not part of the public API.
 * Use `BarChart` for all bar chart needs.
 */
import React, { forwardRef, RefObject, useMemo } from 'react';
import classNames from 'classnames';

import {
  Comp,
  isComponent,
  RedsiftColorGreenD1,
  RedsiftColorNeutralBlack,
  RedsiftColorRedD1,
  useTheme,
} from '@redsift/design-system';

import { BarChartSectionProps } from './types';
import { StyledBarChartSection } from './styles';
import { BarChartContext, useBarChartContext } from './context';
import { getAxisType } from '../Axis';

const COMPONENT_NAME = 'BarChartSection';
const CLASSNAME = 'redsift-barchart-section';

export const BarChartSection: Comp<BarChartSectionProps, SVGGElement> = forwardRef((props, ref) => {
  const {
    className,
    from = 0,
    to = 1,
    color,
    title,
    titlePosition = 'center',
    scaleX,
    scaleY,
    children: propsChildren,
    direction,
    regions,
    ...forwardedProps
  } = props;

  const { textColor, backgroundColor } = useMemo(() => {
    if (color === 'green') {
      return {
        textColor: RedsiftColorGreenD1,
        backgroundColor: '#F7FBF9',
      };
    } else if (color === 'red') {
      return {
        textColor: RedsiftColorRedD1,
        backgroundColor: '#FFF7F7',
      };
    } else if (color === undefined) {
      return {
        textColor: RedsiftColorNeutralBlack,
        backgroundColor: undefined,
      };
    }
    return color;
  }, [color]);

  const theme = useTheme();

  const context = useBarChartContext();
  const {
    chartHeight,
    chartWidth,
    margins,
    disableAnimations,
    xAxisTickValues,
    xAxisTickPadding,
    xAxisTickRotation,
    xAxisTickSize,
    xAxisTickFormat,
    xAxisTickRemodelling,
    xAxisVariant,
    yAxisTickValues,
    yAxisTickPadding,
    yAxisTickRotation,
    yAxisTickSize,
    yAxisTickFormat,
    yAxisTickRemodelling,
    yAxisVariant,
  } = context;

  const sectionHeight = Math.max(0, Math.min(1, to - from)) * chartHeight;
  const sectionY = Math.max(0, Math.min(1, from)) * chartHeight;

  const titleX = titlePosition === 'center' ? chartWidth / 2 : titlePosition === 'right' ? chartWidth - 10 : 10;

  const titleAnchor = titlePosition === 'center' ? 'middle' : titlePosition === 'right' ? 'end' : 'start';

  const children = useMemo(() => {
    const renderedChildren: React.ReactElement[] = [];

    React.Children.forEach(propsChildren, (child) => {
      if (!React.isValidElement(child)) return;
      const isX = getAxisType(child.props.position) === 'x';

      let enhancedChild = child;

      if (isComponent('Axis')(child)) {
        enhancedChild = React.cloneElement(child, {
          key: child.key || `axis-${from}-${to}-${child.props.position}`,
          chartWidth,
          chartHeight,
          sectionHeight,
          scale: isX ? scaleX : scaleY,
          x: child.props.position === 'right' ? chartWidth : 0,
          y: child.props.position === 'bottom' ? sectionHeight : child.props.position === 'top' ? 0 : sectionHeight,
          disableAnimations,
          length: isX ? chartWidth : sectionHeight,
          tickValues: (isX ? xAxisTickValues : yAxisTickValues) ?? 8,
          tickPadding: isX ? xAxisTickPadding : yAxisTickPadding,
          tickRotation: isX ? xAxisTickRotation : yAxisTickRotation,
          tickSize: isX ? xAxisTickSize : yAxisTickSize,
          tickFormat: isX ? xAxisTickFormat : yAxisTickFormat,
          tickRemodelling: isX ? xAxisTickRemodelling : yAxisTickRemodelling,
          variant: isX ? xAxisVariant : yAxisVariant,
          textColor: isX ? undefined : color,
          tickToColor: (_: never, index: number) => index !== 0,
          ...child.props,
        });
      }

      if (isComponent('BarChartOverlay')(child)) {
        enhancedChild = React.cloneElement(child, {
          key: child.key || `overlay-${from}-${to}`,
          from,
          to,
          ...child.props,
        });
      }

      renderedChildren.push(enhancedChild);
    });

    return renderedChildren;
  }, [propsChildren]);

  return (
    <BarChartContext.Provider
      value={{
        ...context,
        scaleX: scaleX!,
        scaleY: scaleY!,
        direction,
        sectionHeight,
        regions,
      }}
    >
      <StyledBarChartSection
        {...forwardedProps}
        className={classNames(BarChartSection.className, className)}
        transform={`translate(${margins.left},${(margins.top || 0) + sectionY})`}
        ref={ref as RefObject<SVGGElement>}
        $theme={theme}
        $textColor={textColor}
      >
        {backgroundColor ? <rect x={0} y={0} width={chartWidth} height={sectionHeight} fill={backgroundColor} /> : null}
        {regions
          ? regions.map((region, index) => {
              // Calculate previous and next day in ms
              const DAY_MS = 24 * 60 * 60 * 1000;
              const fromTime = Date.parse(region.from as string);
              const toTime = Date.parse(region.to as string);
              const prevTime = fromTime - DAY_MS;
              const nextTime = toTime + DAY_MS;
              // Midpoints
              const midFrom = (fromTime + prevTime) / 2;
              const midTo = (toTime + nextTime) / 2;
              const regionFrom = typeof region.from === 'number' ? region.from : scaleX?.(midFrom as any);
              const regionTo = typeof region.to === 'number' ? region.to : scaleX?.(midTo as any);
              if (regionFrom === undefined || regionTo === undefined) {
                return null;
              }
              const x = Math.min(regionFrom, regionTo);
              const width = Math.abs(regionTo - regionFrom);
              return (
                <rect
                  key={`region-${index}`}
                  x={x}
                  y={0}
                  width={width}
                  height={sectionHeight}
                  fill={region.backgroundColor || 'transparent'}
                  opacity={region.backgroundOpacity || 0.05}
                />
              );
            })
          : null}
        {title && (
          <text
            className="title colored"
            x={titleX}
            y={direction === 'up' ? 8 : direction === 'down' ? sectionHeight - 8 : sectionHeight / 2}
            textAnchor={titleAnchor}
            fontSize={12}
            dominantBaseline={direction === 'up' ? 'hanging' : direction === 'down' ? 'auto' : 'middle'}
          >
            {title}
          </text>
        )}
        {children}
      </StyledBarChartSection>
    </BarChartContext.Provider>
  );
});

BarChartSection.className = CLASSNAME;
BarChartSection.displayName = COMPONENT_NAME;
